import React, { useState, useEffect } from "react";
import { Box, Typography, Button, useTheme, Paper, Grid } from "@mui/material";
import { fetchCityImage } from "../../utils/fetchCityImage";
import CachedIcon from "@mui/icons-material/Cached";

// A reliable, public domain generic travel image to use if everything else fails
const FALLBACK_IMAGE_URL =
  "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=800&auto=format&fit=crop";

const ExploreCityCarousel = ({
  exploredCities = [],
  onCityClick,
  selectedCity,
  prefetchedExploreStays = {},
  mediansByCity = {},
  onExploreMore,
  transportMode,
  onFlightSearch,
  tofly,
  departureDate,
  returnDate,
  currency, // ✅ Added currency prop
}) => {
  const theme = useTheme();

  const displayCities = exploredCities.slice(0, 4);

  const numDays =
    departureDate && returnDate
      ? Math.ceil(
          (new Date(returnDate) - new Date(departureDate)) /
            (1000 * 60 * 60 * 24)
        )
      : null;

  const [imageUrls, setImageUrls] = useState([]);

  // 1. Fetch Images
  useEffect(() => {
    let mounted = true;
    const cities = exploredCities.slice(0, 4);
    Promise.all(cities.map((c) => fetchCityImage(c.city, c.country))).then(
      (urls) => {
        if (mounted) setImageUrls(urls);
      }
    );
    return () => {
      mounted = false;
    };
  }, [exploredCities]);

  return (
    <Box sx={{ textAlign: "center", px: 2 }}>
      <Typography
        component="h2"
        variant="h3"
        sx={{
          fontWeight: 900,
          fontSize: { xs: "2rem", sm: "2.5rem", md: "2rem" },
          mb: 1, // Reduced margin
          textAlign: "center",
          letterSpacing: "-0.02em",
          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          filter:
            theme.palette.mode === "dark"
              ? "drop-shadow(0 2px 10px rgba(255,255,255,0.1))"
              : "drop-shadow(0 2px 5px rgba(0,0,0,0.1))",
        }}
      >
        FlyBnB Suggested Destinations
      </Typography>

      {/* ✅ UPDATED BUTTON: Text variant, no outline, right below title */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
        <Button
          variant="text"
          size="small"
          onClick={onExploreMore}
          startIcon={<CachedIcon fontSize="small" />}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.9rem",
            color: "text.secondary",
            "&:hover": {
              color: "primary.main",
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          Generate different destinations
        </Button>
      </Box>

      <Grid container spacing={2} justifyContent="center" alignItems="stretch">
        {displayCities.map((city, idx) => {
          const key = `${city.city},${city.country}`;
          const median = mediansByCity[key] ?? null;

          // ✅ CURRENCY FORMATTER
          // Automatically handles symbol (C$, €, £) based on 'currency' prop
          const formattedPrice = median
            ? new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: currency || "USD",
                maximumFractionDigits: 0,
              }).format(median)
            : null;

          return (
            <Grid item key={idx} xs={12} sm={6} lg={3}>
              <Paper
                elevation={1}
                onClick={() => {
                  onCityClick(city);
                }}
                sx={{
                  p: 0,
                  borderRadius: "24px",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#2c2c2c" : "#f5f5f5",
                  outline:
                    selectedCity?.city === city.city &&
                    selectedCity?.country === city.country
                      ? "3px solid #5F5AA2"
                      : "1px solid transparent",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={imageUrls[idx] || FALLBACK_IMAGE_URL}
                  alt={`${city.city}, ${city.country}`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = FALLBACK_IMAGE_URL;
                  }}
                  sx={{
                    width: "100%",
                    height: 140,
                    objectFit: "cover",
                    borderRadius: "24px 24px 0 0",
                    mb: 2,
                    backgroundColor: "#e0e0e0",
                  }}
                />

                <Box
                  sx={{
                    p: 1,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: theme.palette.text.primary,
                    }}
                  >
                    {city.city}, {city.country}
                  </Typography>

                  {city.estimatedTravelTime && (
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
                    >
                      {(city.transportMode || transportMode) === "train"
                        ? "🚆"
                        : (city.transportMode || transportMode) === "car"
                          ? "🚗"
                          : "✈️"}{" "}
                      ~ {city.estimatedTravelTime}{" "}
                      {(city.transportMode || transportMode) === "train"
                        ? "train"
                        : (city.transportMode || transportMode) === "car"
                          ? "drive"
                          : "flight"}
                    </Typography>
                  )}

                  {/* ✅ DISPLAY FORMATTED CURRENCY 
                  {median != null ? (
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
                    >
                      🏠 ~{formattedPrice}
                      {numDays != null ? ` for ${numDays} days` : ""}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontStyle: "italic",
                        mt: 0.5,
                      }}
                    >
                      🏠 Loading…
                    </Typography>
                  )}
                    */}

                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontStyle: "italic",
                      mt: 1,
                      mb: 2,
                      flexGrow: 1,
                    }}
                  >
                    {city.why || "Popular with travelers this season."}
                  </Typography>

                  <Button
                    variant="contained"
                    sx={{
                      mb: 1,
                      borderRadius: "20px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCityClick(city);
                    }}
                  >
                    Search
                  </Button>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ExploreCityCarousel;
